import React, { createRef, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Grid, type Theme, useMediaQuery } from '@mui/material'
import type { Nil } from '@pbt/pbt-ui-components'

import { KibSearchWrapper } from '~/components/common/search/KibSearchWrapper'
import {
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
} from '~/store/reducers/orders'
import type { Order } from '~/types'
import { TrackingEvent, useTrackSearch } from '~/utils/trackingProvider'

import { useSwapProductSearch } from '../hooks/useSwapProductSearch'
import SwapRxSearchResults from './SwapRxSearchResults'

const SwapRxSearch = ({
  patientId,
  clientId,
  selectedOrder,
  setSelectedOrder,
}: {
  clientId: string | Nil
  patientId: string | Nil
  selectedOrder: Order | null
  setSelectedOrder: (selectedOrder: Order) => void
}) => {
  const { addSearchAttempt, trackSearch, startSearch } = useTrackSearch(
    TrackingEvent.SEARCH_CHARGE_TAB,
    'selectedItemVariationId',
  )

  const isMedium = useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg'))

  const searchResults = useSelector(getOrdersSearchResults)
  const searchResultsTotalCount = useSelector(getOrdersSearchResultsTotalCount)

  const itemsRefs = Array.from({ length: searchResults.length }, () =>
    createRef<any>(),
  )

  const validate = () =>
    itemsRefs.every((ref) =>
      ref.current?.validate ? ref.current.validate() : true,
    )

  const {
    onSearchChange,
    onSearchBlur,
    onSearchFocus,
    searchDisabled,
    searchTerm,
    onSearchMore,
    onSearchClear,
  } = useSwapProductSearch({
    validate,
    patientId,
    clientId,
    onSearch: addSearchAttempt,
    onFocus: startSearch,
  })

  // Use refs to store tracked values to avoid tracking when search term changes
  const searchTermRef = useRef(searchTerm)
  const searchResultsTotalCountRef = useRef(searchResultsTotalCount)

  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])

  useEffect(() => {
    searchResultsTotalCountRef.current = searchResultsTotalCount
  }, [searchResultsTotalCount])

  return (
    <Grid container p={2}>
      <Grid
        container
        item
        alignItems="baseline"
        width="100%"
        wrap={isMedium ? 'wrap-reverse' : 'nowrap'}
        zIndex={1}
      >
        <Grid container position="relative">
          <KibSearchWrapper
            showCloseButton
            disabled={searchDisabled}
            placeholder="Search catalog"
            value={searchTerm}
            onBlur={onSearchBlur}
            onChange={onSearchChange}
            onClear={onSearchClear}
            onFocus={onSearchFocus}
          />
          {patientId && clientId && (
            <SwapRxSearchResults
              clearSearch={onSearchClear}
              searchTerm={searchTerm}
              selectedOrder={selectedOrder}
              setSelectedOrder={setSelectedOrder}
              onSearch={onSearchChange}
              onSearchMore={onSearchMore}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default SwapRxSearch
